/**
 * Детальный просмотр лога графа. Раскрывающиеся секции:
 * - Граф (summary)
 * - Агенты/узлы (с длительностью справа)
 * - Изменения параметров (при COMPLETED) — внизу
 *
 * Используется в ChatsPage и LogsPage.
 */
import { useState } from 'react'
import { useI18n } from '../i18n'
import StructuredDataView from './StructuredDataView'
import { AGENT_KEYS, NODES } from '../config/graphEntities'
import { formatDurationMs, formatInvokedAt } from '../utils/logFormatters'
import '../styles/components/GraphLogDetail.css'

/** Маппинг node_name (из логов) → ключ перевода для отображения. */
const NODE_LABEL_KEYS = {}
AGENT_KEYS.forEach((k) => {
  NODE_LABEL_KEYS[k] = `settings.agent.label.${k}`
})
NODES.forEach((n) => {
  NODE_LABEL_KEYS[n.key] = `chats.logs.nodeLabel.${n.key}`
})
NODE_LABEL_KEYS.parallel_analyzer_memory = 'chats.logs.nodeLabel.parallel_analyzer_memory'
NODE_LABEL_KEYS.policy_safety_action = 'chats.logs.nodeLabel.policy_safety_action'
NODE_LABEL_KEYS.send_message = 'chats.logs.nodeLabel.send'
// Legacy: script_generator переименован в message_generator — старые логи показывают то же название
NODE_LABEL_KEYS.script_generator = 'settings.agent.label.message_generator'

function Collapsible({ title, children, defaultOpen = false, rightSlot, className = '', nested }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className={`graph-log-collapsible ${className} ${open ? 'graph-log-collapsible--open' : ''} ${nested ? 'graph-log-collapsible--nested' : ''}`}>
      <button
        type="button"
        className="graph-log-collapsible-header"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="graph-log-collapsible-icon">{open ? '▼' : '▶'}</span>
        <span className="graph-log-collapsible-title">{title}</span>
        {rightSlot && <span className="graph-log-collapsible-right">{rightSlot}</span>}
      </button>
      {open && <div className="graph-log-collapsible-body">{children}</div>}
    </div>
  )
}

function JsonBlock({ data, compact }) {
  if (data === null || data === undefined) return null
  return <StructuredDataView value={data} compact={compact} className="graph-log-data-view" />
}

function DiffBlock({ diffByKey, t }) {
  const keys = Object.keys(diffByKey || {})
  if (keys.length === 0) {
    return <p className="graph-log-param-empty">{t('chats.logs.paramsNoChanges')}</p>
  }
  return (
    <ul className="graph-log-param-diff-list">
      {keys.sort().map((k) => (
        <li key={k}>
          <span className="graph-log-param-diff-key">{k}</span>
          <span className="graph-log-param-diff-summary">{diffByKey[k]?.summary ?? '—'}</span>
        </li>
      ))}
    </ul>
  )
}

function ParameterChangeTabs({ parameter_changes, parameter_diff, t }) {
  const [paramTab, setParamTab] = useState('before')
  const l3b = parameter_changes?.l3?.before
  const l3a = parameter_changes?.l3?.after
  const l4b = parameter_changes?.l4?.before
  const l4a = parameter_changes?.l4?.after
  const d3 = parameter_diff?.l3 || {}
  const d4 = parameter_diff?.l4 || {}

  const beforeDoc = { l3: { before: l3b ?? null }, l4: { before: l4b ?? null } }
  const afterDoc = { l3: { after: l3a ?? null }, l4: { after: l4a ?? null } }

  const tabs = [
    { id: 'before', label: t('chats.logs.paramsBefore') },
    { id: 'after', label: t('chats.logs.paramsAfter') },
    { id: 'changes', label: t('chats.logs.paramsChanges') },
  ]

  return (
    <div className="graph-log-param-tabs">
      <div className="graph-log-param-tab-row" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={paramTab === tab.id}
            className={`graph-log-param-tab ${paramTab === tab.id ? 'graph-log-param-tab--active' : ''}`}
            onClick={() => setParamTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="graph-log-param-tab-panel" role="tabpanel">
        {paramTab === 'before' && <JsonBlock data={beforeDoc} />}
        {paramTab === 'after' && <JsonBlock data={afterDoc} />}
        {paramTab === 'changes' && (
          <div className="graph-log-param-changes-wrap">
            <div className="graph-log-param-sub">{t('chats.logs.paramsL3')}</div>
            <DiffBlock diffByKey={d3} t={t} />
            <div className="graph-log-param-sub">{t('chats.logs.paramsL4')}</div>
            <DiffBlock diffByKey={d4} t={t} />
          </div>
        )}
      </div>
    </div>
  )
}

function AgentNodeContent({ node, aiCall, getNodeDuration, getNodeDurationMs, t, lang, JsonBlock, graphInstance }) {
  const isParallel = node.node_name === 'parallel_analyzer_memory'
  const request = aiCall?.request_payload ?? node.input
  const response = aiCall?.response_payload ?? node.output
  const isSendMessage = node.node_name === 'send_message'
  const delaySeconds =
    (node.output && typeof node.output === 'object' ? node.output.delay_seconds : null) ??
    (graphInstance?.node_results?.send_message && typeof graphInstance.node_results.send_message === 'object'
      ? graphInstance.node_results.send_message.delay_seconds
      : null)

  return (
    <>
      {node.error && (
        <div className="graph-log-agent-error">
          <strong>{t('chats.logs.status')}:</strong> {node.status} — {node.error}
        </div>
      )}
      {node.error && (
        <div className="graph-log-agent-error">{node.error}</div>
      )}
      {isSendMessage && (
        <div className="graph-log-agent-timing">
          <Collapsible title={t('chats.logs.duration')} defaultOpen={true}>
            <JsonBlock
              data={{
                duration: getNodeDuration(node),
                duration_ms: getNodeDurationMs(node),
                ...(delaySeconds != null && { delay_seconds: delaySeconds, duration_with_delay: formatDurationMs((getNodeDurationMs(node) ?? 0) + delaySeconds * 1000, lang) }),
              }}
            />
          </Collapsible>
        </div>
      )}
      {request != null && (
        <Collapsible title={t('chats.logs.request')} defaultOpen={false}>
          {isParallel && typeof request === 'object' && (request.message_analyzer != null || request.memory_summary != null) ? (
            <>
              {request.message_analyzer != null && (
                <Collapsible title={t('settings.agent.label.message_analyzer')} defaultOpen={false} nested>
                  <JsonBlock data={request.message_analyzer} />
                </Collapsible>
              )}
              {request.memory_summary != null && (
                <Collapsible title={t('settings.agent.label.memory_summary')} defaultOpen={false} nested>
                  <JsonBlock data={request.memory_summary} />
                </Collapsible>
              )}
            </>
          ) : (
            <JsonBlock data={request} />
          )}
        </Collapsible>
      )}
      {response != null && (
        <Collapsible title={t('chats.logs.response')} defaultOpen={false}>
          {isParallel && typeof response === 'object' && (response.message_analyzer != null || response.memory_summary != null) ? (
            <>
              {response.message_analyzer != null && (
                <Collapsible title={t('settings.agent.label.message_analyzer')} defaultOpen={false} nested>
                  <JsonBlock data={response.message_analyzer} />
                </Collapsible>
              )}
              {response.memory_summary != null && (
                <Collapsible title={t('settings.agent.label.memory_summary')} defaultOpen={false} nested>
                  <JsonBlock data={response.memory_summary} />
                </Collapsible>
              )}
            </>
          ) : (
            <JsonBlock data={response} />
          )}
        </Collapsible>
      )}
    </>
  )
}

function getSendMessageDurationLabel(node, t, lang, graphInstance) {
  if (node.node_name !== 'send_message') return null
  const delaySeconds =
    (node.output && typeof node.output === 'object' ? node.output.delay_seconds : null) ??
    (graphInstance?.node_results?.send_message && typeof graphInstance.node_results.send_message === 'object'
      ? graphInstance.node_results.send_message.delay_seconds
      : null)
  const durationMs = node.duration_ms ?? 0
  if (delaySeconds != null && delaySeconds > 0) {
    const totalMs = durationMs + delaySeconds * 1000
    return `${formatDurationMs(totalMs, lang)} (${t('chats.logs.delaySeconds', { n: delaySeconds })})`
  }
  return formatDurationMs(durationMs, lang) || null
}

function monetizationSummaryFromNodes(nodesList) {
  const core = (nodesList || []).find((n) => n.node_name === 'core' || n.node_name === 'core_brain')
  const c = core?.output?.constraints
  if (!c || typeof c !== 'object') return null
  const out = {}
  if ('allow_offer' in c) out.allow_offer = Boolean(c.allow_offer)
  if ('allow_chat_media' in c) out.allow_chat_media = Boolean(c.allow_chat_media)
  return Object.keys(out).length ? out : null
}

export default function GraphLogDetail({ logData }) {
  const { t, lang } = useI18n()
  if (!logData) return null

  const [copying, setCopying] = useState(false)
  const [copyingPreview, setCopyingPreview] = useState(false)

  const handleCopyFullLog = async () => {
    if (copying) return
    setCopying(true)
    try {
      const text = JSON.stringify(logData, null, 2)
      await navigator.clipboard.writeText(text)
      alert(t('common.copied') || 'Copied to clipboard')
    } catch (err) {
      console.error('Failed to copy full graph log:', err)
      alert(t('common.error') || 'Error')
    } finally {
      setCopying(false)
    }
  }

  const {
    graph_instance: gi,
    nodes: rawNodes = [],
    ai_calls = [],
    parameter_changes = {},
    parameter_diff = {},
    condition_preview = {},
    monetization_summary: monetizationSummaryRaw = null,
  } = logData
  const status = gi?.status || ''
  const showParams = status === 'COMPLETED' || status === 'FAILED' || status === 'ROLLBACK'

  // Убираем дубли: RUNNING + COMPLETED на один узел — оставляем COMPLETED (или последнюю запись)
  const nodesByKey = new Map()
  rawNodes.forEach((n) => {
    const key = n.node_name
    const existing = nodesByKey.get(key)
    const finalStatuses = ['COMPLETED', 'FAILED', 'SKIPPED']
    const preferNew = !existing || (finalStatuses.includes(n.status) && !finalStatuses.includes(existing.status)) || (n.status === existing.status)
    if (preferNew) nodesByKey.set(key, n)
  })
  // Сортируем по started_at — реальный порядок выполнения
  const nodes = Array.from(nodesByKey.values()).sort((a, b) => {
    const ta = a.started_at ? new Date(a.started_at).getTime() : 0
    const tb = b.started_at ? new Date(b.started_at).getTime() : 0
    return ta - tb
  })

  const monetizationSummary = monetizationSummaryRaw || monetizationSummaryFromNodes(nodes)

  const aiCallsByAgent = {}
  ;(ai_calls || []).forEach((ac) => {
    const k = ac.agent_type || 'unknown'
    if (!aiCallsByAgent[k]) aiCallsByAgent[k] = []
    aiCallsByAgent[k].push(ac)
  })

  const nodeNames = nodes.map((n) => n.node_name)
  const ranParallel = nodeNames.includes('message_analyzer') && nodeNames.includes('memory_summary')
  const maNode = nodes.find((n) => n.node_name === 'message_analyzer')
  const msNode = nodes.find((n) => n.node_name === 'memory_summary')
  const maLatency = aiCallsByAgent.message_analyzer?.[0]?.latency_ms ?? maNode?.duration_ms ?? 0
  const msLatency = aiCallsByAgent.memory_summary?.[0]?.latency_ms ?? msNode?.duration_ms ?? 0
  const parallelDurationMs = ranParallel ? Math.max(maLatency || 0, msLatency || 0) : null

  const getNodeLabel = (nodeName) => {
    const key = NODE_LABEL_KEYS[nodeName]
    return key ? t(key) : nodeName
  }

  const getNodeDurationMs = (node) => {
    if (ranParallel && (node.node_name === 'message_analyzer' || node.node_name === 'memory_summary')) {
      return parallelDurationMs
    }
    const ac = (aiCallsByAgent[node.node_name] || [])[0]
    return ac?.latency_ms ?? node.duration_ms ?? null
  }

  const getNodeDuration = (node) => {
    const ms = getNodeDurationMs(node)
    return formatDurationMs(ms, lang)
  }

  // SAFETY_LOCK — ответ заблокирован по risk_flag (policy_risk_score >= 70 и т.п.)
  const isSafetyLock = (gi?.state_after || '').toUpperCase() === 'SAFETY_LOCK'
  const analyzerNode = nodes.find((n) => n.node_name === 'message_analyzer')
  const riskReason = analyzerNode?.output?.risk_reason || 'risk_flag'
  const isFailed = status === 'FAILED'
  const graphError = gi?.error

  return (
    <div className="graph-log-detail">
      <div className="graph-log-copy-row">
        <button
          type="button"
          className="graph-log-copy-btn"
          onClick={handleCopyFullLog}
          disabled={copying}
          title={t('chats.logs.copyLog') || 'Copy log'}
        >
          {copying ? '...' : '📋'}
        </button>
      </div>
      {isFailed && graphError && (
        <div className="graph-log-failed-banner" role="alert">
          <span className="graph-log-failed-icon">✕</span>
          <div className="graph-log-failed-text">
            <strong>{t('chats.logs.failedReason')}</strong>
            <span>{graphError}</span>
          </div>
        </div>
      )}
      {isSafetyLock && (
        <div className="graph-log-safety-lock-banner" role="alert">
          <span className="graph-log-safety-lock-icon">⚠</span>
          <div className="graph-log-safety-lock-text">
            <strong>{t('chats.logs.safetyLockBanner')}</strong>
            <span>{t('chats.logs.safetyLockReason', { reason: riskReason })}</span>
          </div>
        </div>
      )}
      <Collapsible title={t('chats.logs.agents')} defaultOpen={true} className="graph-log-agents-section">
        <ul className="graph-log-agents-list">
          {nodes.length === 0 ? (
            <li className="graph-log-agents-empty">{t('chats.logs.noAgents')}</li>
          ) : (
            nodes.map((node, idx) => (
              <li key={`${node.node_name}-${idx}`} className="graph-log-agent-item">
                <Collapsible
                  title={
                    <span className="graph-log-agent-title-wrap">
                      <span className="graph-log-collapsible-title">{getNodeLabel(node.node_name)}</span>
                      <span className="graph-log-agent-meta">{formatInvokedAt(node.started_at) ?? '—'}</span>
                    </span>
                  }
                  defaultOpen={false}
                  rightSlot={
                    <span className="graph-log-agent-badges">
                      <span className={`graph-log-agent-status-badge graph-log-agent-status-badge--${(node.status || '').toLowerCase()}`} data-status={node.status}>
                        {node.status || '—'}
                      </span>
                      <span className="graph-log-agent-duration-badge">
                        {getSendMessageDurationLabel(node, t, lang, gi) ?? getNodeDuration(node) ?? '—'}
                      </span>
                    </span>
                  }
                  className="graph-log-agent-collapsible"
                >
                  <div className="graph-log-agent-content">
                    <AgentNodeContent
                      node={node}
                      aiCall={(aiCallsByAgent[node.node_name] || [])[0]}
                      getNodeDuration={getNodeDuration}
                      getNodeDurationMs={getNodeDurationMs}
                      t={t}
                      lang={lang}
                      JsonBlock={JsonBlock}
                      graphInstance={gi}
                    />
                  </div>
                </Collapsible>
              </li>
            ))
          )}
        </ul>
      </Collapsible>

      {showParams && Object.keys(parameter_changes).length > 0 && (
        <Collapsible
          title={t('chats.logs.parameters')}
          defaultOpen={false}
          className="graph-log-params-section"
        >
          <div className="graph-log-params-content">
            {condition_preview?.at_allow_offer && Object.keys(condition_preview.at_allow_offer).length > 0 && (
              <div className="graph-log-condition-preview-bar">
                <button
                  type="button"
                  className="graph-log-copy-btn graph-log-copy-btn--text"
                  disabled={copyingPreview}
                  onClick={async () => {
                    if (copyingPreview) return
                    setCopyingPreview(true)
                    try {
                      const text = JSON.stringify(condition_preview.at_allow_offer, null, 2)
                      await navigator.clipboard.writeText(text)
                      alert(t('common.copied') || 'Copied')
                    } catch (e) {
                      console.error(e)
                      alert(t('common.error') || 'Error')
                    } finally {
                      setCopyingPreview(false)
                    }
                  }}
                >
                  {copyingPreview ? '…' : t('chats.logs.copyConditionPreview')}
                </button>
                {condition_preview.source && (
                  <span className="graph-log-condition-preview-meta">
                    {condition_preview.source === 'core_snapshot' ||
                    condition_preview.source === 'legacy_merged_after'
                      ? t(`chats.logs.conditionPreviewSource.${condition_preview.source}`)
                      : condition_preview.source}
                  </span>
                )}
                {monetizationSummary && typeof monetizationSummary === 'object' && (
                  <div className="graph-log-monetization-summary" style={{ marginTop: '0.5rem' }}>
                    <span className="graph-log-condition-preview-meta">
                      {t('chats.logs.monetizationSummaryTitle')}
                      {': '}
                      {'allow_offer' in monetizationSummary && (
                        <span style={{ marginLeft: '0.35rem' }}>
                          {t('chats.logs.monetizationSummaryAllowOffer')}=
                          <strong>{String(monetizationSummary.allow_offer)}</strong>
                        </span>
                      )}
                      {'allow_chat_media' in monetizationSummary && (
                        <span style={{ marginLeft: '0.5rem' }}>
                          {t('chats.logs.monetizationSummaryAllowChatMedia')}=
                          <strong>{String(monetizationSummary.allow_chat_media)}</strong>
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}
            <ParameterChangeTabs
              parameter_changes={parameter_changes}
              parameter_diff={parameter_diff}
              t={t}
            />
            <Collapsible title={t('chats.logs.rawParameterJson')} defaultOpen={false} nested className="graph-log-raw-json-wrap">
              <JsonBlock data={parameter_changes} />
            </Collapsible>
          </div>
        </Collapsible>
      )}
    </div>
  )
}
